import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { UserProfile } from "@shared/types";
import { PageItem } from "@shared/types";
import { formatDate } from "@/lib/utils";
import { useLocation } from "@/contexts/LocationContext";

interface PagesProps {
  user: UserProfile;
}

export default function Pages({ user }: PagesProps) {
  const { selectedLocationIds, locations, setSelectedLocationIds } = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<PageItem | null>(null);

  // Get location name helper
  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  // Get display title for current selection
  const getDisplayTitle = () => {
    if (selectedLocationIds.includes('all')) {
      return 'All Locations';
    } else if (selectedLocationIds.length === 1) {
      return getLocationName(selectedLocationIds[0]);
    } else {
      return `${selectedLocationIds.length} Locations Combined`;
    }
  };

  // Get pages with multi-location support
  const { data: allPages, isLoading } = useQuery<PageItem[]>({
    queryKey: ['pages', selectedLocationIds],
    queryFn: async () => {
      console.log('📄 Loading pages for locations:', selectedLocationIds);
      
      if (selectedLocationIds.includes('all')) {
        // Load all pages
        const response = await fetch('/api/pages', { redirect: 'error' });
        if (!response.ok) throw new Error('Failed to fetch pages');
        return response.json();
      } else if (selectedLocationIds.length === 1) {
        // Single location
        const response = await fetch(`/api/pages?locationId=${selectedLocationIds[0]}`, { redirect: 'error' });
        if (!response.ok) throw new Error('Failed to fetch pages');
        return response.json();
      } else {
        // Multi-select: load all pages and filter
        const response = await fetch('/api/pages', { redirect: 'error' });
        if (!response.ok) throw new Error('Failed to fetch pages');
        const allPages = await response.json();
        
        // Filter pages for selected locations
        const filteredPages = allPages.filter((page: PageItem) => {
          return page.locationId && selectedLocationIds.includes(page.locationId);
        });
        
        console.log(`📄 Filtered ${filteredPages.length} pages from ${allPages.length} total`);
        return filteredPages;
      }
    },
    enabled: selectedLocationIds.length > 0
  });

  // Filter and process pages
  const pages = allPages || [];

  // Filter pages based on search and active tab
  const filterPages = (pages: PageItem[]) => {
    return pages.filter(page => {
      const matchesSearch = searchQuery === "" || 
        page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.content.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTab = selectedCategory === "all" || 
        (selectedCategory === "templates" && page.isTemplate) ||
        (selectedCategory === "documents" && !page.isTemplate);
      
      return matchesSearch && matchesTab;
    });
  };

  // Mock data for development
  const mockPages: PageItem[] = [
    {
      id: '1',
      title: 'COVID-19 Safety Protocol',
      content: 'Detailed guidelines for patient and staff safety during the pandemic...',
      lastModified: new Date().toISOString(),
      author: 'Sarah Johnson',
      isTemplate: false
    },
    {
      id: '2',
      title: 'New Patient Onboarding',
      content: 'Step-by-step process for onboarding new patients, including paperwork and initial assessment...',
      lastModified: new Date(Date.now() - 2 * 86400000).toISOString(),
      author: 'Michael Chen',
      isTemplate: true
    },
    {
      id: '3',
      title: 'Staff Training Manual',
      content: 'Comprehensive training guide for new hires covering all clinic procedures...',
      lastModified: new Date(Date.now() - 5 * 86400000).toISOString(),
      author: 'Emily Rodriguez',
      isTemplate: false
    },
    {
      id: '4',
      title: 'Equipment Maintenance Schedule',
      content: 'Schedule and procedures for regular maintenance of all clinical equipment...',
      lastModified: new Date(Date.now() - 7 * 86400000).toISOString(),
      author: 'David Thompson',
      isTemplate: false
    },
    {
      id: '5',
      title: 'Monthly Report Template',
      content: 'Standardized template for monthly performance reports by department...',
      lastModified: new Date(Date.now() - 14 * 86400000).toISOString(),
      author: 'Sarah Johnson',
      isTemplate: true
    }
  ];

  // Locations are fetched from API

  const displayLocations = locations || [];
  const displayPages = pages || mockPages;
  const filteredPages = filterPages(displayPages);

  // Get the icon based on whether it's a template
  const getPageIcon = (isTemplate: boolean) => {
    return isTemplate ? 'mdi-file-document-edit-outline' : 'mdi-file-document-outline';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Pages</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="SOPs, training documents, and internal protocols"
          ></span>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Select
              value={selectedLocationIds.join(',')}
              onValueChange={(value) => {
                const newLocationIds = value.split(',');
                setSelectedLocationIds(newLocationIds);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {displayLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button>
            <span className="mdi mdi-plus mr-2"></span>
            New Page
          </Button>
        </div>
      </div>

      {/* Search and Tabs */}
      <div className="flex flex-col sm:flex-row justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="relative max-w-md w-full">
          <Input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10"
          />
          <span className="mdi mdi-magnify absolute right-3 top-2.5 text-neutral-400"></span>
        </div>
        
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="all">All Pages</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Pages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, index) => (
            <Skeleton key={index} className="h-40 w-full" />
          ))
        ) : (
          filteredPages.map((page) => (
            <Card key={page.id} className="overflow-hidden">
              <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between">
                <div className="flex items-start space-x-2">
                  <span className={`mdi ${getPageIcon(page.isTemplate)} text-primary text-xl mt-0.5`}></span>
                  <div>
                    <CardTitle className="text-lg">{page.title}</CardTitle>
                    <p className="text-xs text-neutral-500 mt-1">
                      {page.isTemplate ? 'Template' : 'Document'} • Updated {formatDate(page.lastModified)}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <span className="mdi mdi-dots-vertical"></span>
                </Button>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-300 line-clamp-3">{page.content}</p>
              </CardContent>
              <CardFooter className="p-4 pt-0 flex justify-between">
                <span className="text-xs text-neutral-500">Created by: {page.author}</span>
                <Button variant="outline" size="sm">Open</Button>
              </CardFooter>
            </Card>
          ))
        )}
      </div>

      {filteredPages.length === 0 && !isLoading && (
        <Card className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">No Pages Found</h3>
          <p className="text-neutral-500 dark:text-neutral-400 mb-4">
            {searchQuery ? 
              `No pages match your search for "${searchQuery}"` : 
              "No pages have been created for this location yet."}
          </p>
          <Button>
            <span className="mdi mdi-plus mr-2"></span>
            Create New Page
          </Button>
        </Card>
      )}
    </div>
  );
}
